/**
 * Case Templates tab - CRUD for case email templates (in Settings)
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { CaseTemplate, CASE_EMAIL_PLACEHOLDERS, BODY_PLACEHOLDER } from '../types/case';
import apiService from '../services/api';

interface CaseTemplatesTabProps {
  onError?: (msg: string) => void;
}

const CaseTemplatesTab: React.FC<CaseTemplatesTabProps> = ({ onError }) => {
  const [templates, setTemplates] = useState<CaseTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CaseTemplate | null>(null);
  const [form, setForm] = useState({ name: '', subject: '', body: '', from_email: '', from_name: '', is_active: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await apiService.getCaseTemplates();
      setTemplates(res.templates || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleOpenAdd = () => {
    setEditing(null);
    setForm({ name: '', subject: '', body: '', from_email: '', from_name: '', is_active: true });
    setDialogOpen(true);
  };

  const handleOpenEdit = (t: CaseTemplate) => {
    setEditing(t);
    setForm({
      name: t.name,
      subject: t.subject || '',
      body: t.body,
      from_email: t.from_email || '',
      from_name: t.from_name || '',
      is_active: t.is_active,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.body.trim()) {
      setError('Name and body are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editing) {
        await apiService.updateCaseTemplate(editing.id, {
          name: form.name,
          subject: form.subject || undefined,
          body: form.body,
          from_email: form.from_email.trim() || undefined,
          from_name: form.from_name.trim() || undefined,
          is_active: form.is_active,
        });
      } else {
        await apiService.createCaseTemplate({
          name: form.name,
          subject: form.subject || undefined,
          body: form.body,
          from_email: form.from_email.trim() || undefined,
          from_name: form.from_name.trim() || undefined,
          is_active: form.is_active,
        });
      }
      setDialogOpen(false);
      fetchTemplates();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await apiService.deleteCaseTemplate(id);
      fetchTemplates();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to delete template');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Case email templates</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>
          Add template
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Active</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      No case templates yet
                    </TableCell>
                  </TableRow>
                ) : (
                  templates.map((t) => (
                    <TableRow key={t.id} hover>
                      <TableCell>{t.name}</TableCell>
                      <TableCell>{t.subject || '-'}</TableCell>
                      <TableCell>{t.is_active ? 'Yes' : 'No'}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenEdit(t)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(t.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit template' : 'Add template'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              disabled={!!editing}
            />
            <TextField
              label="Subject (optional)"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              fullWidth
              placeholder="e.g. Re: {{case_number}} – {{subject}}"
            />
            <TextField
              label="From email (optional)"
              value={form.from_email}
              onChange={(e) => setForm({ ...form, from_email: e.target.value })}
              fullWidth
              placeholder="e.g. cases@sonicmail.net.au – overrides MAILGUN_FROM_EMAIL"
            />
            <TextField
              label="From name (optional)"
              value={form.from_name}
              onChange={(e) => setForm({ ...form, from_name: e.target.value })}
              fullWidth
              placeholder="e.g. Sonic Cases – overrides MAILGUN_FROM_NAME"
            />
            <TextField
              label="Body"
              required
              multiline
              rows={8}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              fullWidth
              placeholder={`Use ${BODY_PLACEHOLDER} for user message; {{case_number}}, {{subject}}, etc. for case data`}
            />
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Placeholders
              </Typography>
              <Typography variant="caption" component="span" sx={{ display: 'block', mb: 0.5 }}>
                Subject &amp; body: {CASE_EMAIL_PLACEHOLDERS.join(', ')}
              </Typography>
              <Typography variant="caption" component="span" sx={{ display: 'block' }}>
                Body only: {BODY_PLACEHOLDER} (where the user&apos;s message is inserted when sending)
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={24} /> : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CaseTemplatesTab;
